import { Check, ChevronDown, Info } from 'lucide-react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/* ------------------------------------------------------------------ buttons */

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'md' | 'sm';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-[#0140ba] disabled:bg-primary/50',
  secondary: 'border border-white text-white hover:bg-white/10',
  danger: 'bg-error text-white hover:bg-[#c40000]',
  ghost: 'text-on-surface-variant hover:bg-white/10',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  md: 'h-12 px-5 text-base',
  sm: 'h-9 px-3 text-sm',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  hint,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize; hint?: string }) {
  const button = (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${BUTTON_SIZES[size]} ${BUTTON_VARIANTS[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
  if (!hint) return button;
  return (
    <span className="inline-flex flex-col items-start gap-1">
      {button}
      <span className="text-xs text-outline">{hint}</span>
    </span>
  );
}

/* -------------------------------------------------------------------- chips */

export type BadgeVariant =
  | 'outline'
  | 'primary'
  | 'info'
  | 'success'
  | 'error'
  | 'jupiter'
  | 'resale'
  | 'callisto'
  | 'neptune'
  | 'ganymede'
  | 'white'
  | 'dark';

const BADGE_VARIANTS: Record<BadgeVariant, string> = {
  outline: 'border border-outline text-on-surface-variant',
  primary: 'bg-primary text-white',
  // Chip « Aujourd'hui ! » de l'app : dégradé bleu #0C83C7 → #026CDF (77 %).
  info: 'bg-[linear-gradient(90deg,#0C83C7_0%,#026CDF_77%)] text-white',
  success: 'bg-success text-white',
  error: 'bg-error text-white',
  jupiter: 'bg-jupiter text-bg',
  resale: 'bg-resale text-white',
  callisto: 'bg-callisto text-white',
  neptune: 'bg-neptune text-white',
  ganymede: 'bg-ganymede text-bg',
  white: 'bg-white text-bg',
  dark: 'bg-bg text-white',
};

/** Chip de statut : radius 4, padding 6/10, 14px semibold (docs/design-tokens.md). */
export function Badge({
  variant = 'outline',
  className = '',
  title,
  children,
}: {
  variant?: BadgeVariant;
  className?: string;
  /** Infobulle native — sert aux chips qui expliquent une conséquence. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm/4 font-semibold ${BADGE_VARIANTS[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

/* ----------------------------------------------------------- cards/sections */

export type CardTone = 'surface' | 'neutral';

const CARD_TONES: Record<CardTone, string> = {
  surface: 'bg-surface border border-card-border',
  neutral: 'bg-surface-neutral border border-card-border',
};

export function Card({
  tone = 'neutral',
  children,
  className = '',
  id,
}: {
  tone?: CardTone;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <div id={id} className={`rounded-lg p-4 shadow-elevation-3 ${CARD_TONES[tone]} ${className}`}>
      {children}
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-outline">{children}</h2>;
}

export function Section({
  title,
  actions,
  children,
  id,
  tone = 'neutral',
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
  id?: string;
  tone?: CardTone;
}) {
  return (
    <section id={id} className={`rounded-lg shadow-elevation-3 ${CARD_TONES[tone]}`}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <SectionTitle>{title}</SectionTitle>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------- page heading */

/** Top bar façon app : titre 24px bold, ligne 1px #999 en bas. */
export function PageHeader({ title, actions }: { title: string; actions?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-outline pb-4">
      <h1 className="text-2xl/6 font-bold tracking-[0.48px] text-white">{title}</h1>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/* -------------------------------------------------------- segmented control */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

/** Bouton segmenté de l'app : bordure blanche, h-40, onglet actif blanc + coche. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  className = '',
}: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={`inline-flex h-10 items-stretch overflow-hidden rounded-lg border border-white ${className}`}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={`inline-flex items-center gap-1.5 px-4 text-sm font-semibold transition ${
              active ? 'bg-white text-bg' : 'text-on-surface-variant hover:bg-white/10'
            }`}
          >
            {active ? <Check size={14} aria-hidden /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ switch */

/**
 * Interrupteur on/off. Le nom accessible est exactement `label` : l'état est
 * porté par `aria-checked`, et le texte « levé / baissé » n'est qu'un rappel visuel.
 */
export function Switch({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  onLabel = 'levé',
  offLabel = 'baissé',
  showState = true,
  className = '',
}: {
  label: string;
  /** Petite ligne secondaire sous le libellé (ex. l'effet technique). */
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  onLabel?: string;
  offLabel?: string;
  /** Afficher le texte d'état à côté du libellé (inutile quand le switch suffit). */
  showState?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-start gap-2.5 rounded-lg px-1 py-1 text-left text-sm transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
      <span
        aria-hidden
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
          checked ? 'bg-primary' : 'bg-surface-variant'
        }`}
      >
        <span
          className={`absolute size-4 rounded-full bg-white transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`}
        />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-semibold text-on-surface-variant">{label}</span>
          {showState ? (
            <span aria-hidden className={`text-xs font-semibold ${checked ? 'text-primary-container' : 'text-outline'}`}>
              {checked ? onLabel : offLabel}
            </span>
          ) : null}
        </span>
        {description ? <span className="text-xs/4 font-normal text-outline">{description}</span> : null}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ banners */

export function InfoBanner({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg bg-primary-container px-3 py-2.5 text-sm font-semibold text-on-primary-container ${className}`}
    >
      <Info size={16} aria-hidden className="mt-0.5 shrink-0" />
      <div className="min-w-0 font-normal">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------- champs */

/**
 * Label + control. The hint is a tooltip on the label (not a line below) so every field keeps the
 * same height and controls stay aligned in `items-end` rows.
 */
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="flex items-center gap-1 font-semibold text-on-surface-variant" title={hint}>
        {label}
        {hint ? (
          <span aria-label={hint} className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-outline text-[10px] leading-none text-outline">
            i
          </span>
        ) : null}
      </span>
      {children}
    </label>
  );
}

const CONTROL =
  'h-10 rounded-lg border border-surface-variant bg-field px-3 py-2 text-sm text-white placeholder:text-outline outline-none focus:border-primary-container focus:ring-1 focus:ring-primary-container disabled:opacity-50';

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${CONTROL} ${className}`} {...props} />;
}

/**
 * Select au thème : flèche native masquée (collée au bord, non stylable) et remplacée par un chevron
 * placé à 12px du bord, avec la place réservée à droite (`pr-9`) pour que le texte ne passe pas dessous.
 */
export function Select({ className = '', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  // Une largeur explicite (ex. `w-20`) s'applique au conteneur ; sinon le select prend toute la largeur.
  const width = className.split(/\s+/).filter((c) => /^w-/.test(c)).join(' ') || 'w-full';
  const rest = className.split(/\s+/).filter((c) => !/^w-/.test(c)).join(' ');
  return (
    <span className={`relative inline-flex ${width}`}>
      <select className={`${CONTROL} w-full appearance-none pr-9 ${rest}`} {...props}>
        {children}
      </select>
      <ChevronDown
        size={16}
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-outline"
      />
    </span>
  );
}

export function Textarea({ className = '', ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${CONTROL} h-auto min-h-20 ${className}`} {...props} />;
}

export function Checkbox({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="checkbox"
      className={`size-4 accent-primary ${className}`}
      {...props}
    />
  );
}

/* ---------------------------------------------------------------- tableaux */

export const TABLE = 'w-full text-left text-sm text-on-surface-variant';
export const THEAD_ROW = 'border-b border-line text-xs uppercase tracking-[0.12em] text-outline';
export const TH = 'px-3 py-2 font-semibold';
export const ROW = 'border-b border-line transition hover:bg-hover';
export const TD = 'px-3 py-2';

/* ------------------------------------------------------------------- états */

export function Spinner({ label = 'Chargement…' }: { label?: string }) {
  return <p className="py-6 text-center text-sm text-outline">{label}</p>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div role="alert" className="rounded-lg border border-error bg-error/10 px-3 py-2 text-sm text-error-container">
      {message}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-outline">{children}</p>;
}

/* ------------------------------------------------------------------ tabs */

export interface TabOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Onglets soulignés (navigation de page), distincts du SegmentedControl (filtre).
 * Flèches gauche / droite pour passer d'un onglet à l'autre (pattern ARIA tablist).
 */
export function Tabs<T extends string>({
  options,
  value,
  onChange,
  label,
  idPrefix,
}: {
  options: readonly TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  /** Préfixe des id `tab-*` / `panel-*` pour relier onglets et panneaux. */
  idPrefix: string;
}) {
  const move = (delta: number) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + delta + options.length) % options.length];
    if (next) {
      onChange(next.value);
      document.getElementById(`${idPrefix}-tab-${next.value}`)?.focus();
    }
  };
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 border-b border-card-border">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            id={`${idPrefix}-tab-${option.value}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${idPrefix}-panel-${option.value}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') move(1);
              if (e.key === 'ArrowLeft') move(-1);
            }}
            className={`-mb-px border-b-2 px-4 py-2.5 text-base font-semibold transition-colors ${
              active ? 'border-primary text-white' : 'border-transparent text-outline hover:text-white'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
